import React, { createContext, useContext, useState, useEffect } from 'react';
import api, { passkeyApi } from '../services/api';
import { getCredential } from '../utils/webauthn';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    try {
      const token = localStorage.getItem('authToken');
      const savedUser = localStorage.getItem('authUser');
      if (token && savedUser) {
        setUser(JSON.parse(savedUser));
      }
    } catch (error) {
      console.warn('Error reading auth state from localStorage:', error);
      localStorage.removeItem('authToken');
      localStorage.removeItem('authUser');
    } finally {
      setLoading(false);
    }
  }, []);

  const acceptSession = ({ token, username: uname, roles }) => {
    localStorage.setItem('authToken', token);
    const userData = { username: uname, roles };
    localStorage.setItem('authUser', JSON.stringify(userData));
    setUser(userData);
    return userData;
  };

  const login = async (username, password) => {
    const response = await api.post('/auth/login', { username, password });
    return acceptSession(response.data);
  };

  /**
   * The passkey is discoverable, so the authenticator says which account it belongs to and nothing
   * is typed. The server answers with the same token shape as a password login.
   */
  const loginWithPasskey = async () => {
    const { data: ceremony } = await passkeyApi.authenticationOptions();
    const credential = await getCredential(ceremony.options);
    const response = await passkeyApi.authenticate({
      ceremonyId: ceremony.ceremonyId,
      credential,
    });
    return acceptSession(response.data);
  };

  const logout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('authUser');
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, loginWithPasskey, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};
